import React, { useState, useEffect } from 'react';
import analytics from '../../services/analytics';
import { VisitorTrackingService } from '../../services/visitorTracking';

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState({
    pageViews: [],
    events: [],
    purchases: []
  });
  const [visitorStats, setVisitorStats] = useState(null);
  const [stats, setStats] = useState({});
  const [timeRange, setTimeRange] = useState('7'); // days
  const [loading, setLoading] = useState(true);
  const [realTimeVisitors, setRealTimeVisitors] = useState({
    total: 0,
    mobile: 0,
    desktop: 0,
    tablet: 0
  });

  useEffect(() => {
    loadAnalyticsData();
    loadVisitorData();
    
    // Set up real-time visitor tracking
    const interval = setInterval(() => {
      updateRealTimeVisitors();
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, [timeRange]);

  const updateRealTimeVisitors = async () => {
    try {
      // Get Firebase realtime visitor data
      const firebaseRealtime = await VisitorTrackingService.getRealtimeVisitors();
      setRealTimeVisitors(firebaseRealtime);
    } catch (error) {
      console.error('Error updating realtime visitors:', error);
      // Fallback to local data
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      const data = analytics.getAnalyticsData();
      const recentVisitors = data.pageViews.filter(pv => pv.timestamp > fiveMinutesAgo);
      const uniqueVisitors = new Set(recentVisitors.map(pv => pv.userAgent)).size;
      setRealTimeVisitors({ total: uniqueVisitors, mobile: 0, desktop: 0, tablet: 0 });
    }
  };

  const loadVisitorData = async () => {
    try {
      const stats = await VisitorTrackingService.getVisitorStats(parseInt(timeRange));
      setVisitorStats(stats);
    } catch (error) {
      console.error('Error loading visitor data:', error);
    }
  };

  const loadAnalyticsData = () => {
    setLoading(true);
    try {
      const data = analytics.getAnalyticsData();
      const filtered = filterDataByTimeRange(data, parseInt(timeRange));
      setAnalyticsData(filtered);
      setStats(calculateStats(filtered));
    } catch (error) {
      console.error('Error loading analytics data:', error);
    }
    setLoading(false);
  };

  const filterDataByTimeRange = (data, days) => {
    const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
    
    return {
      pageViews: data.pageViews.filter(item => item.timestamp > cutoff),
      events: data.events.filter(item => item.timestamp > cutoff),
      purchases: data.purchases.filter(item => item.timestamp > cutoff)
    };
  };

  const calculateStats = (data) => {
    const stats = {
      totalPageViews: data.pageViews.length,
      uniquePages: new Set(data.pageViews.map(pv => pv.path)).size,
      totalEvents: data.events.length,
      totalPurchases: data.purchases.length,
      totalRevenue: data.purchases.reduce((sum, p) => sum + (p.total || 0), 0),
      avgOrderValue: 0,
      topPages: [],
      topEvents: [],
      topBeans: [],
      menuInteractions: [],
      cartInteractions: [],
      locationClicks: [],
      deviceTypes: {},
      hourlyActivity: new Array(24).fill(0),
      dailyVisitors: {},
      bounceRate: 0,
      avgSessionDuration: 0
    };

    if (stats.totalPurchases > 0) {
      stats.avgOrderValue = stats.totalRevenue / stats.totalPurchases;
    }

    // Top pages
    const pageGroups = {};
    data.pageViews.forEach(pv => {
      pageGroups[pv.path] = (pageGroups[pv.path] || 0) + 1;
    });
    stats.topPages = Object.entries(pageGroups)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([path, count]) => ({ path, count }));

    // Top events
    const eventGroups = {};
    data.events.forEach(event => {
      const key = `${event.category} - ${event.action}`;
      eventGroups[key] = (eventGroups[key] || 0) + 1;
    });
    stats.topEvents = Object.entries(eventGroups)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([event, count]) => ({ event, count }));

    // Device types (simplified)
    data.pageViews.forEach(pv => {
      if (pv.userAgent) {
        const isMobile = /Mobi|Android/i.test(pv.userAgent);
        const device = isMobile ? 'Mobile' : 'Desktop';
        stats.deviceTypes[device] = (stats.deviceTypes[device] || 0) + 1;
      }
    });

    // Hourly activity
    [...data.pageViews, ...data.events].forEach(item => {
      const hour = new Date(item.timestamp).getHours();
      stats.hourlyActivity[hour]++;
    });

    // Bean/Product interactions
    const beanInteractions = {};
    const menuInteractions = data.events.filter(e => e.category === 'Menu');
    const cartInteractions = data.events.filter(e => e.category === 'Cart');
    const locationInteractions = data.events.filter(e => e.category === 'Location');

    // Track popular beans/products
    [...menuInteractions, ...cartInteractions].forEach(event => {
      if (event.label) {
        beanInteractions[event.label] = (beanInteractions[event.label] || 0) + 1;
      }
    });

    stats.topBeans = Object.entries(beanInteractions)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([bean, interactions]) => ({ bean, interactions }));

    stats.menuInteractions = menuInteractions.length;
    stats.cartInteractions = cartInteractions.length;
    stats.locationClicks = locationInteractions.length;

    // Daily visitor tracking
    data.pageViews.forEach(pv => {
      const date = new Date(pv.timestamp).toDateString();
      stats.dailyVisitors[date] = (stats.dailyVisitors[date] || 0) + 1;
    });

    // Calculate bounce rate (single page visits)
    const sessions = {};
    data.pageViews.forEach(pv => {
      const sessionKey = pv.userAgent + '_' + new Date(pv.timestamp).toDateString();
      sessions[sessionKey] = (sessions[sessionKey] || 0) + 1;
    });
    
    const singlePageSessions = Object.values(sessions).filter(count => count === 1).length;
    stats.bounceRate = Object.keys(sessions).length > 0 ? 
      Math.round((singlePageSessions / Object.keys(sessions).length) * 100) : 0;

    // Calculate average session duration (simplified)
    if (data.events.length > 0) {
      const sessionDurations = data.events
        .filter(e => e.category === 'Engagement' && e.action === 'time_on_page')
        .map(e => e.value || 0);
      
      if (sessionDurations.length > 0) {
        const avgMs = sessionDurations.reduce((sum, duration) => sum + duration, 0) / sessionDurations.length;
        stats.avgSessionDuration = Math.round(avgMs / 1000 / 60); // Convert to minutes
      }
    }

    return stats;
  };

  const exportData = () => {
    analytics.exportUserData();
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
      analytics.deleteUserData();
      loadAnalyticsData();
      alert('All analytics data has been cleared.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-QA', {
      style: 'currency',
      currency: 'QAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const StatCard = ({ title, value, subtitle, icon, color = '#333' }) => (
    <div style={{
      background: '#fff',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #f0f0f0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
        <h3 style={{ 
          margin: 0, 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#666',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {title}
        </h3>
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color, marginBottom: '4px' }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '12px', color: '#888' }}>
          {subtitle}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        color: '#666'
      }}>
        Loading analytics data...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '28px', 
            fontWeight: '700',
            color: '#222'
          }}>
            Analytics Dashboard
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            User behavior and website performance insights
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
          </select>
          
          <button
            onClick={exportData}
            style={{
              padding: '8px 16px',
              background: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Export Data
          </button>
          
          <button
            onClick={clearAllData}
            style={{
              padding: '8px 16px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear Data
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <StatCard
          icon="🟢"
          title="Live Visitors"
          value={realTimeVisitors.total.toLocaleString()}
          subtitle={`📱${realTimeVisitors.mobile} 💻${realTimeVisitors.desktop} 📟${realTimeVisitors.tablet}`}
          color="#28a745"
        />
        
        <StatCard
          icon="👀"
          title="Page Views"
          value={stats.totalPageViews.toLocaleString()}
          subtitle={`${stats.uniquePages} unique pages`}
        />
        
        <StatCard
          icon="☕"
          title="Bean Interest"
          value={stats.menuInteractions.toLocaleString()}
          subtitle="Menu interactions"
          color="#8B4513"
        />
        
        <StatCard
          icon="🛒"
          title="Cart Actions"
          value={stats.cartInteractions.toLocaleString()}
          subtitle="Add to cart events"
          color="#FF6B35"
        />
        
        <StatCard
          icon="📍"
          title="Location Clicks"
          value={stats.locationClicks.toLocaleString()}
          subtitle="Location interest"
          color="#007bff"
        />
        
        <StatCard
          icon="📊"
          title="Bounce Rate"
          value={`${stats.bounceRate}%`}
          subtitle="Single page visits"
          color={stats.bounceRate > 70 ? "#dc3545" : stats.bounceRate > 50 ? "#ffc107" : "#28a745"}
        />
        
        <StatCard
          icon="⏱️"
          title="Avg Session"
          value={`${stats.avgSessionDuration}m`}
          subtitle="Time on site"
          color="#6f42c1"
        />
        
        <StatCard
          icon="💰"
          title="Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subtitle={`${stats.totalPurchases} orders`}
          color="#28a745"
        />

        {/* Firebase Visitor Stats */}
        {visitorStats && (
          <>
            <StatCard
              icon="📱"
              title="Mobile Visitors"
              value={visitorStats.mobileVisitors.toLocaleString()}
              subtitle={`${Math.round((visitorStats.mobileVisitors / visitorStats.totalVisitors) * 100)}% of total`}
              color="#007bff"
            />
            
            <StatCard
              icon="💻"
              title="Desktop Visitors"
              value={visitorStats.desktopVisitors.toLocaleString()}
              subtitle={`${Math.round((visitorStats.desktopVisitors / visitorStats.totalVisitors) * 100)}% of total`}
              color="#6f42c1"
            />
            
            <StatCard
              icon="👥"
              title="Unique Visitors"
              value={visitorStats.uniqueVisitors.toLocaleString()}
              subtitle={`${visitorStats.newVisitors} new, ${visitorStats.returningVisitors} returning`}
              color="#fd7e14"
            />
          </>
        )}
      </div>

      {/* Charts and Tables */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px'
      }}>
        {/* Top Pages */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Top Pages
          </h3>
          {stats.topPages.map((page, index) => (
            <div key={page.path} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: index < stats.topPages.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}>
              <span style={{ fontSize: '14px', color: '#333' }}>
                {page.path || '/'}
              </span>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#666'
              }}>
                {page.count}
              </span>
            </div>
          ))}
        </div>

        {/* Popular Beans/Products */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ☕ Popular Beans
          </h3>
          {stats.topBeans.length > 0 ? (
            stats.topBeans.map((bean, index) => (
              <div key={bean.bean} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: index < stats.topBeans.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                    {bean.bean}
                  </span>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {bean.interactions} interactions
                  </div>
                </div>
                <div style={{
                  width: '60px',
                  height: '6px',
                  background: '#f0f0f0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min((bean.interactions / stats.topBeans[0].interactions) * 100, 100)}%`,
                    height: '100%',
                    background: '#8B4513',
                    borderRadius: '3px'
                  }} />
                </div>
              </div>
            ))
          ) : (
            <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
              No bean interactions recorded yet
            </p>
          )}
        </div>

        {/* Top Events */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Top Events
          </h3>
          {stats.topEvents.map((event, index) => (
            <div key={event.event} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: index < stats.topEvents.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}>
              <span style={{ fontSize: '14px', color: '#333' }}>
                {event.event}
              </span>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#666'
              }}>
                {event.count}
              </span>
            </div>
          ))}
        </div>

        {/* Device Types (Local Analytics) */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Device Types (Local)
          </h3>
          {Object.entries(stats.deviceTypes).map(([device, count]) => (
            <div key={device} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0'
            }}>
              <span style={{ fontSize: '14px', color: '#333' }}>
                {device}
              </span>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#666'
              }}>
                {count} ({Math.round((count / stats.totalPageViews) * 100)}%)
              </span>
            </div>
          ))}
        </div>

        {/* Firebase Device Breakdown */}
        {visitorStats && (
          <div style={{
            background: '#fff',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔥 Live Device Analytics
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>📱</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Mobile</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#007bff' }}>
                    {visitorStats.mobileVisitors}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {Math.round((visitorStats.mobileVisitors / visitorStats.totalVisitors) * 100)}%
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>💻</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Desktop</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#6f42c1' }}>
                    {visitorStats.desktopVisitors}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {Math.round((visitorStats.desktopVisitors / visitorStats.totalVisitors) * 100)}%
                  </div>
                </div>
              </div>
              
              {visitorStats.tabletVisitors > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>📟</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Tablet</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#fd7e14' }}>
                      {visitorStats.tabletVisitors}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {Math.round((visitorStats.tabletVisitors / visitorStats.totalVisitors) * 100)}%
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{
                borderTop: '1px solid #dee2e6',
                paddingTop: '12px',
                marginTop: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#495057' }}>Total Visitors</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#28a745' }}>
                    {visitorStats.totalVisitors}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Visitors */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Daily Visitors
          </h3>
          <div style={{ 
            display: 'flex', 
            alignItems: 'end', 
            gap: '3px', 
            height: '100px',
            marginTop: '12px'
          }}>
            {Object.entries(stats.dailyVisitors).slice(-7).map(([date, visitors]) => {
              const maxVisitors = Math.max(...Object.values(stats.dailyVisitors));
              const height = maxVisitors > 0 ? (visitors / maxVisitors) * 80 : 0;
              const dayName = new Date(date).toLocaleDateString('en', { weekday: 'short' });
              
              return (
                <div
                  key={date}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      height: `${height}px`,
                      width: '100%',
                      background: visitors > 0 ? '#007bff' : '#f0f0f0',
                      borderRadius: '3px 3px 0 0',
                      minHeight: '3px'
                    }}
                    title={`${dayName} - ${visitors} visitors`}
                  />
                  <span style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                    {dayName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity by Hour */}
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
            Activity by Hour
          </h3>
          <div style={{ 
            display: 'flex', 
            alignItems: 'end', 
            gap: '2px', 
            height: '100px',
            marginTop: '12px'
          }}>
            {stats.hourlyActivity.map((activity, hour) => {
              const maxActivity = Math.max(...stats.hourlyActivity);
              const height = maxActivity > 0 ? (activity / maxActivity) * 80 : 0;
              
              return (
                <div
                  key={hour}
                  style={{
                    flex: 1,
                    height: `${height}px`,
                    background: activity > 0 ? '#333' : '#f0f0f0',
                    borderRadius: '2px 2px 0 0',
                    position: 'relative',
                    minHeight: '2px'
                  }}
                  title={`${hour}:00 - ${activity} activities`}
                />
              );
            })}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '12px',
            color: '#666'
          }}>
            <span>0</span>
            <span>6</span>
            <span>12</span>
            <span>18</span>
            <span>23</span>
          </div>
        </div>
      </div>

      {/* Data Summary */}
      <div style={{
        background: '#f8f9fa',
        padding: '24px',
        borderRadius: '12px',
        marginTop: '24px'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
          Data Collection Status
        </h3>
        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
          <p style={{ margin: '0 0 8px 0' }}>
            📊 Collecting anonymous usage data to improve user experience
          </p>
          <p style={{ margin: '0 0 8px 0' }}>
            🔒 All data is stored locally and respects user privacy preferences
          </p>
          <p style={{ margin: 0 }}>
            🚀 Use insights to optimize menu layout, popular products, and user flow
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 